import React, { useState, useEffect } from 'react';
import { Box, Input, Typography, Select, MenuItem } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import FilterListIcon from '@mui/icons-material/FilterList';
import Layout from '../../../../components/Layout';
import AdminNavbar from '../../../../components/Admin/AdminNavbar/AdminNavbar';
import { PayoutTable } from '../../../../components/Admin/Payments/Payout/PayoutTable';
import './Payout.css';
import toast from 'react-hot-toast';
import { axiosInstance } from '../../../../utils/tokenUtils';

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const Payout = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [payoutData, setPayoutData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [rbacMe, setRbacMe] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [statusFilter, setStatusFilter] = useState('Pending');
  const [sortField, setSortField] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  const handleChangeSearch = e => setSearchTerm(e.target.value);

  const handleStatusFilterChange = event => {
    setStatusFilter(event.target.value);
    setCurrentPage(1);
  };

  const handleSortFieldChange = event => {
    setSortField(event.target.value);
    setCurrentPage(1);
  };

  const handleSortOrderChange = event => {
    setSortOrder(event.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = newPage => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = event => {
    setPageSize(event.target.value);
    setCurrentPage(1);
  };

  // Helper function to check if user has a specific permission
  const hasPermission = permission => {
    if (!rbacMe || !Array.isArray(rbacMe.permissions)) return false;
    return rbacMe.permissions.includes(permission);
  };

  // Check if user can download payout reports
  const canDownloadPayout = () => {
    return hasPermission('download_bank_transaction_report');
  };

  const handleDownload = async () => {
    if (isDownloading || statusFilter !== 'Pending') return;

    // Check permission first
    if (!canDownloadPayout()) {
      toast.dismiss();
      toast.error(
        "You don't have permission to download payout reports. Please contact your administrator."
      );
      return;
    }

    const toastId = toast.loading('Preparing download...');
    try {
      setIsDownloading(true);
      const { data } = await axiosInstance.post(
        '/admin/transactions/download-pending-payout-requests',
        { teacher_ids: [] }
      );
      const url = data?.url;
      if (url) {
        const link = document.createElement('a');
        link.href = url;
        link.download = '';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('Downloaded successfully');
      } else {
        toast.error('No download URL returned');
      }
    } catch (error) {
      toast.error('Failed to start download');
    } finally {
      toast.dismiss(toastId);
      setIsDownloading(false);
    }
  };

  const generatePaginationItems = () => {
    const items = [];
    const maxVisiblePages = 5;

    if (totalPages <= 1) {
      return items;
    }

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        items.push(i);
      }
    } else {
      items.push(1);

      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      if (start > 2) {
        items.push('...');
      }

      for (let i = start; i <= end; i++) {
        items.push(i);
      }

      if (end < totalPages - 1) {
        items.push('...');
      }

      if (totalPages > 1) {
        items.push(totalPages);
      }
    }

    return items;
  };

  // Load current user permissions
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        let cachedData = null;
        try {
          const cached = sessionStorage.getItem('rbac_admin_me');
          if (cached) {
            cachedData = JSON.parse(cached);
            setRbacMe(cachedData);
          }
        } catch (_) {}

        const { data } = await axiosInstance.get('/admin/rbac/admin/me');
        setRbacMe(data);

        try {
          sessionStorage.setItem('rbac_admin_me', JSON.stringify(data));
        } catch (_) {}
      } catch (_) {
        if (!rbacMe) setRbacMe({ roles: [], permissions: [] });
      }
    };

    fetchCurrentUser();
  }, []);

  useEffect(() => {
    const fetchPayouts = async () => {
      try {
        setIsLoading(true);
        const apiStatus = statusFilter ? statusFilter.toLowerCase() : undefined;
        const sortPrefix = sortOrder === 'desc' ? '-' : '';
        const params = {
          search_query: debouncedSearchTerm?.trim() || undefined,
          page: currentPage,
          page_size: pageSize,
          status: apiStatus,
          sort_fields: `${sortPrefix}${sortField}`,
        };
        const { data } = await axiosInstance.get('/admin/transactions/payout-requests', {
          params,
        });
        const mapped = (data?.results || []).map(item => ({
          id: item.id,
          teacherId: item.teacher_id,
          name:
            `${item.first_name || ''}${item.last_name ? ' ' + item.last_name : ''}`.trim() ||
            '-'.trim(),
          instructorType: item.instructor_type || '-',
          totalSessions: item.total_sessions || 0,
          walletAmount: Number(item.wallet_amount || 0),
          requestedAmount: Number(item.requested_amount || 0),
          status:
            (item.status || 'pending').toLowerCase() === 'success'
              ? 'Success'
              : (item.status || 'pending').toLowerCase() === 'failed'
                ? 'Failed'
                : 'Pending',
          userImage: item.profile_image || '/placeholder.svg',
        }));
        setPayoutData(mapped);
        const count = Number(data?.count || 0);
        setTotalCount(count);
        setTotalPages(Math.max(1, Math.ceil(count / pageSize)));
      } catch (error) {
        toast.error('Failed to fetch payout requests');
      } finally {
        setIsLoading(false);
      }
    };
    fetchPayouts();
  }, [debouncedSearchTerm, statusFilter, sortField, sortOrder, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm]);

  return (
    <Layout title="Klass Online | Payout">
      <Box className="relative h-screen w-full overflow-hidden">
        <Box className="sticky top-0 left-0 right-0 z-[1000] bg-white pr-[10px]">
          <AdminNavbar />
        </Box>
        <Box className="h-[calc(100vh-64px)] overflow-auto relative bg-[#F9F9F9] rounded-lg mr-[10px]">
          <Box className="flex flex-col p-[1rem] gap-4">
            <Box className="flex justify-between items-center">
              <div className="payout-title">Payout Requests</div>
              <Box className="flex items-center gap-4 w-[50%]">
                <Input
                  sx={{
                    textDecoration: 'none',
                    border: '1px solid var(--Stroke, #D5DEE3)',
                    borderRadius: '56px',
                    '&:before, &:after': {
                      borderBottom: 'none !important',
                    },
                    '&:hover:not(.Mui-disabled):before': {
                      borderBottom: 'none !important',
                    },
                    padding: '8px 10px',
                    '& .MuiInputBase-input': {
                      padding: '3px 5px 3px 5px',
                    },
                    width: '100%',
                    height: '40px',
                  }}
                  type="search"
                  placeholder="Search Name, Email or Phone Number"
                  aria-label="Search"
                  value={searchTerm}
                  onChange={handleChangeSearch}
                  startAdornment={<SearchIcon className="payout-search-icon" />}
                />
                <Select
                  value={statusFilter}
                  onChange={handleStatusFilterChange}
                  className="payout-status-filter"
                  sx={{
                    height: '40px',
                    minWidth: '120px',
                    fontFamily: 'Poppins',
                    fontSize: '14px',
                    '& .MuiSelect-select': {
                      padding: '8px 12px',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: '1px solid var(--Stroke, #D5DEE3)',
                      borderRadius: '8px',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      border: '1px solid var(--Stroke, #D5DEE3)',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      border: '1px solid #0a5ef3',
                    },
                  }}
                >
                  <MenuItem value="Pending">Pending</MenuItem>
                  <MenuItem value="Success">Success</MenuItem>
                  <MenuItem value="Failed">Failed</MenuItem>
                </Select>
                <button
                  type="button"
                  className={`payout-download-btn ${statusFilter !== 'Pending' || isDownloading ? 'payout-download-btn-disabled' : ''}`}
                  onClick={handleDownload}
                  disabled={statusFilter !== 'Pending' || isDownloading}
                >
                  <DownloadIcon
                    sx={{
                      color: statusFilter !== 'Pending' || isDownloading ? '#999' : '#fff',
                      fontSize: 18,
                    }}
                  />
                  <Typography
                    className={`payout-download-btn-text ${statusFilter !== 'Pending' || isDownloading ? 'payout-download-btn-text-disabled' : ''}`}
                  >
                    {isDownloading ? 'Preparing...' : 'Download'}
                  </Typography>
                </button>
              </Box>
            </Box>

            <Box className="payout-sort-content">
              <Typography className="payout-sort-label">Sort by</Typography>
              <Select
                value={sortField}
                onChange={handleSortFieldChange}
                className="payout-sort-select"
                displayEmpty
                sx={{
                  height: '32px',
                  minWidth: '100px',
                  fontFamily: 'Poppins',
                  fontSize: '13px',
                  '& .MuiSelect-select': {
                    padding: '6px 10px',
                    color: '#666',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: '1px solid #ccc',
                    borderRadius: '6px',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    border: '1px solid #ccc',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    border: '1px solid #ccc',
                  },
                  '& .MuiSelect-icon': {
                    color: '#666',
                    fontSize: '18px',
                  },
                }}
              >
                <MenuItem value="created_at">Created Date</MenuItem>
                <MenuItem value="wallet_amount">Wallet Amount</MenuItem>
              </Select>

              <Select
                value={sortOrder}
                onChange={handleSortOrderChange}
                className="payout-sort-select"
                sx={{
                  height: '32px',
                  minWidth: '80px',
                  fontFamily: 'Poppins',
                  fontSize: '13px',
                  '& .MuiSelect-select': {
                    padding: '6px 10px',
                    color: '#666',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: '1px solid #ccc',
                    borderRadius: '6px',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    border: '1px solid #ccc',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    border: '1px solid #ccc',
                  },
                  '& .MuiSelect-icon': {
                    color: '#666',
                    fontSize: '18px',
                  },
                }}
              >
                <MenuItem value="desc">Desc</MenuItem>
                <MenuItem value="asc">Asc</MenuItem>
              </Select>
            </Box>

            <PayoutTable
              data={payoutData}
              isLoading={isLoading}
              selectedItems={[]}
              onSelectItem={() => {}}
            />

            <Box className="payout-pagination-container">
              <Box className="payout-pagination-box">
                <Typography className="payout-pagination-text">Showing</Typography>
                <Select
                  value={pageSize}
                  onChange={handlePageSizeChange}
                  className="payout-pagination-select"
                  sx={{
                    height: '30px',
                    minWidth: '60px',
                    margin: '0 4px',
                    '& .MuiSelect-select': {
                      padding: '6px 24px 6px 8px',
                      textAlign: 'center',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                    '& .MuiSelect-icon': {
                      right: '4px',
                      color: '#666',
                    },
                  }}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={15}>15</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={30}>30</MenuItem>
                </Select>
                <Typography className="payout-pagination-text">
                  of {totalCount} payout requests
                </Typography>
              </Box>

              {generatePaginationItems().length > 0 && (
                <nav>
                  <ul className="payout-pagination-list">
                    <li>
                      <button
                        type="button"
                        className={`payout-pagination-btn ${currentPage === 1 ? 'payout-pagination-btn-disabled' : ''}`}
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </button>
                    </li>

                    {generatePaginationItems().map((item, index) => (
                      <li key={index}>
                        {item === '...' ? (
                          <span className="payout-pagination-ellipsis">…</span>
                        ) : (
                          <button
                            type="button"
                            className={`payout-pagination-btn ${currentPage === item ? 'payout-pagination-btn-selected' : ''}`}
                            onClick={() => handlePageChange(item)}
                          >
                            {item}
                          </button>
                        )}
                      </li>
                    ))}

                    <li>
                      <button
                        type="button"
                        className={`payout-pagination-btn ${currentPage === totalPages ? 'payout-pagination-btn-disabled' : ''}`}
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Layout>
  );
};

export default Payout;
